import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTENT_TYPES = [
  { value: 'vocabulary', label: 'Vocabulary List', description: 'Generate terms and definitions' },
  { value: 'essay_prompt', label: 'Essay Prompt', description: 'Create argumentative essay assignment' },
  { value: 'project_idea', label: 'Project Idea', description: 'Design group project activity' },
  { value: 'present_connections', label: 'Present Connections', description: 'Link historical themes to today' },
];

export const AIContentGenerator = ({ unit, onClose, onContentGenerated }) => {
  const [contentType, setContentType] = useState('vocabulary');
  const [context, setContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');

  const handleGenerate = async () => {
    if (!context.trim()) {
      toast.error('Please provide context for content generation');
      return;
    }

    try {
      setGenerating(true);
      setGeneratedContent('');

      const payload = {
        content_type: contentType,
        context: `Unit: ${unit.title}\nDescription: ${unit.description}\n\nAdditional Context: ${context}`,
        additional_params: {
          grade_level: unit.grade_level,
          unit_number: unit.unit_number
        }
      };

      const response = await axios.post(`${API}/generate-content`, payload);
      setGeneratedContent(response.data.generated_content);
      toast.success('Content generated successfully!');
    } catch (error) {
      toast.error('Failed to generate content. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const selectedType = CONTENT_TYPES.find(t => t.value === contentType);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 no-print">
      <Card className="w-full max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Sparkles className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-2xl font-serif font-normal text-stone-900">AI Content Generator</h2>
                <p className="text-sm text-stone-600">Powered by Gemini AI</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-ai-generator-btn">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content Type Selection */}
          <div className="space-y-2">
            <Label>Content Type</Label>
            <Select value={contentType} onValueChange={setContentType}>
              <SelectTrigger data-testid="content-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div>
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-stone-500">{type.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedType && (
              <p className="text-sm text-stone-600">{selectedType.description}</p>
            )}
          </div>

          {/* Context Input */}
          <div className="space-y-2">
            <Label>Additional Context</Label>
            <Textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Provide specific instructions or context for the AI to generate content..."
              rows={4}
              data-testid="ai-context-input"
            />
            <p className="text-xs text-stone-500">
              Example: &ldquo;Focus on economic causes&rdquo; or &ldquo;Include diverse perspectives&rdquo;
            </p>
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={generating || !context.trim()}
            className="w-full btn-primary"
            data-testid="generate-content-btn"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Content
              </>
            )}
          </Button>

          {/* Generated Content Display */}
          {generatedContent && (
            <div className="space-y-2">
              <Label>Generated Content</Label>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                <pre className="text-sm text-stone-700 whitespace-pre-wrap font-sans">
                  {generatedContent}
                </pre>
              </div>
              <p className="text-xs text-stone-500">
                Review the generated content and copy what you need. You can edit it in the unit editor.
              </p>
            </div>
          )}

          {/* Info Card */}
          <Card className="bg-yellow-50 border-yellow-200 p-4">
            <h3 className="font-semibold text-stone-900 mb-2 text-sm">How to use generated content</h3>
            <ol className="text-xs text-stone-700 space-y-1 list-decimal list-inside">
              <li>Generate content using AI based on your unit context</li>
              <li>Review and copy the generated content</li>
              <li>Go to Edit Unit page to paste and customize the content</li>
              <li>Save your changes to update the curriculum unit</li>
            </ol>
          </Card>
        </div>
      </Card>
    </div>
  );
};

export default AIContentGenerator;
